import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCHF, fmtDate } from "@/lib/api";
import { CHANNEL_ICONS, CHANNEL_LABELS, DOC_STATUS, TRUST, JOB_STATES, ORGAN_LABELS } from "@/lib/organs";
import { SectionTitle } from "@/components/Shared";
import PipelineStepper from "@/components/PipelineStepper";
import ResolveDialog from "@/components/ResolveDialog";

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "processing", label: "In elaborazione" },
  { key: "ready", label: "Pronti" },
  { key: "to_confirm", label: "Da confermare" },
  { key: "anomaly", label: "Anomalie" },
];

export default function Queue() {
  const [docs, setDocs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [engine, setEngine] = useState(null);
  const [resolving, setResolving] = useState(null);

  const load = () => {
    api.get("/documents?archived=false").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/organs/nervous_system").then((r) => setEngine(r.data)).catch(() => {});
    const t = setInterval(load, 5000); // la coda riflette il vero stato backend dei job
    return () => clearInterval(t);
  }, []);

  const retry = async (jobId) => {
    try {
      await api.post(`/jobs/${jobId}/retry`);
      toast.success("Job rimesso in coda: l'elaborazione riparte dall'originale già acquisito.");
      load();
    } catch (e) {
      toast.error("Retry non consentito per questo job.");
    }
  };

  const confirm = async (id) => {
    await api.post(`/documents/${id}/confirm`).catch(() => {});
    toast.success("Documento verificato e archiviato nella Memoria.");
    load();
  };

  const resolveLabel = (d) =>
    (d.pending_fields || []).some((p) => p.field === "payment_method")
      ? "Indica metodo di pagamento"
      : (d.issues || []).some((i) => i.code?.startsWith("duplicate"))
        ? "Risolvi duplicato"
        : "Completa verifica";

  const filtered = filter === "all" ? docs : docs.filter((d) => d.status === filter);

  return (
    <div className="max-w-3xl mx-auto px-4" data-testid="queue-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4"><ChevronLeft size={14} /> Organismo</Link>
      <div className="mb-2 fade-up">
        <p className="text-xs tracking-[0.25em] text-gray-500">SISTEMA NERVOSO · MOTORE DI ELABORAZIONE</p>
        <h1 className="font-display text-3xl font-bold mt-1">Coda di elaborazione</h1>
        <p className="text-sm text-gray-500 mt-1">Un solo motore per ogni canale: scan, email, Drive, upload, banche e POS.</p>
      </div>

      {engine && (
        <div className="grid grid-cols-4 gap-3 my-5 fade-up" data-testid="queue-engine-stats">
          {engine.metrics.map((mt) => (
            <div key={mt.label} className="glass rounded-2xl p-3 text-center">
              <div className="font-display font-bold text-lg">{mt.value}</div>
              <div className="text-[10px] text-gray-500 leading-tight mt-0.5">{mt.label}</div>
            </div>
          ))}
        </div>
      )}

      {jobs.filter((j) => ["queued", "processing", "failed", "retryable"].includes(j.state)).length > 0 && (
        <div className="glass rounded-3xl p-5 mb-5" data-testid="queue-jobs">
          <SectionTitle>Job di elaborazione · stato reale backend</SectionTitle>
          <div className="space-y-2">
            {jobs.filter((j) => ["queued", "processing", "failed", "retryable"].includes(j.state)).map((j) => {
              const js = JOB_STATES[j.state] || JOB_STATES.queued;
              return (
                <div key={j.job_id} data-testid={`job-${j.job_id}`} className="flex items-center gap-3 rounded-2xl px-3 py-2.5 bg-white/[0.02]">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${j.state === "processing" ? "animate-pulse" : ""}`}
                    style={{ background: js.color, boxShadow: `0 0 8px ${js.color}` }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-200 truncate">{j.supplier || j.filename}</div>
                    <div className="text-[11px] text-gray-500">{j.channel} · {js.label}{j.error ? ` · ${j.error}` : ""}</div>
                  </div>
                  {["failed", "retryable"].includes(j.state) && (
                    <button data-testid={`job-retry-${j.job_id}`} onClick={() => retry(j.job_id)} className="btn-ghost !text-[11px] !py-1.5 !px-3">
                      Riprova
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-5" data-testid="queue-filters">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`queue-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`chip !cursor-pointer transition-colors ${filter === f.key ? "!text-cyan-200 !border-cyan-400/50 !bg-cyan-400/10" : ""}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((d, i) => {
          const Icon = CHANNEL_ICONS[d.channel] || CHANNEL_ICONS.file;
          const st = DOC_STATUS[d.status];
          const trust = TRUST[d.trust];
          return (
            <div key={d.doc_id} data-testid={`queue-doc-${d.doc_id}`} className="glass rounded-3xl p-5 fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-cyan-300 shrink-0"
                    style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.15)" }}>
                    <Icon size={18} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{d.supplier || d.filename}</div>
                    <div className="text-xs text-gray-500">{d.doc_type} · {CHANNEL_LABELS[d.channel]} · {fmtDate(d.date)}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm">{fmtCHF(d.amount)}</div>
                  <div className="text-[10px] mt-1" style={{ color: st.color }}>{st.label}</div>
                </div>
              </div>
              {d.note && <div className="mt-3 text-xs text-amber-300/90 bg-amber-400/5 border border-amber-400/15 rounded-xl px-3 py-2">{d.note}</div>}
              {d.pipeline && <div className="mt-3"><PipelineStepper pipeline={d.pipeline} /></div>}
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex gap-1.5 flex-wrap items-center">
                  <span className="chip !text-[10px]" style={{ color: trust?.color }}>{trust?.label}</span>
                  {d.category && <span className="chip !text-[10px] !text-cyan-200">{d.category}{d.learned ? " · appresa" : ""}</span>}
                  {(d.organs || []).slice(0, 4).map((o) => <span key={o} className="chip !text-[10px]">{ORGAN_LABELS[o] || o}</span>)}
                </div>
                {["ready"].includes(d.status) && (
                  <button data-testid={`queue-confirm-${d.doc_id}`} onClick={() => confirm(d.doc_id)} className="btn-ghost !text-[11px] !py-1.5 !px-3">
                    <CheckCircle2 size={12} /> Verifica e archivia
                  </button>
                )}
                {["to_confirm", "anomaly"].includes(d.status) && (
                  <button data-testid={`queue-resolve-${d.doc_id}`} onClick={() => setResolving(d)}
                    className="btn-primary !text-[11px] !py-1.5 !px-3">
                    {resolveLabel(d)}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="glass rounded-3xl p-10 text-center text-gray-500 text-sm" data-testid="queue-empty">
            Nessun documento in questo stato. Il motore è in attesa di nuovo input.
          </div>
        )}
      </div>
      <p className="text-[11px] text-gray-600 mt-6 text-center">Stato reale del motore DAD · i documenti restano visibili anche ad elaborazione in corso.</p>
      <ResolveDialog doc={resolving} onClose={() => setResolving(null)} onResolved={() => load()} />
    </div>
  );
}
